import { Navigate, useRoutes, type RouteObject } from 'react-router-dom';

import { ADMIN_ROLES, SETTINGS_ROLES, landingPathForRole } from '../auth/roles';
import { ForbiddenScreen } from '../auth/ForbiddenScreen';
import { RequireAuth, RequireRole } from '../auth/guards';
import { useSession } from '../auth/session';
import { LandingScreen } from '../screens/LandingScreen';
import { LoginScreen } from '../screens/LoginScreen';
import { NotFoundScreen } from '../screens/NotFoundScreen';
import { ProfileScreen } from '../screens/ProfileScreen';
import { ScreenPlaceholder } from '../screens/ScreenPlaceholder';
import { DashboardScreen } from '../screens/dashboard';
import { DocumentsScreen, DocumentDetailScreen, ExportDownloadScreen } from '../screens/documents';
import { ReviewQueueScreen, ReviewerScreen } from '../screens/review';
import { TaxonomyScreen } from '../screens/taxonomy';
import { AutomationScreen } from '../screens/automation';
import {
  SourcesScreen,
  DesktopUploaderScreen,
  MailboxesScreen,
  LocationsScreen,
  RetentionScreen,
  UsersScreen,
  BrandingScreen,
  WebhooksScreen,
} from '../screens/settings';
import {
  PartnerScreen,
  DocumentFieldsScreen,
  SystemScreen,
  DesktopAppScreen,
  HealthScreen,
} from '../screens/admin';
import { AppShell } from '../shell/AppShell';

import { LOGIN_PATH, SHELL_SCREENS } from './screens';

/** Routes that only admin roles may reach (gated by `RequireRole`). */
const ADMIN_PATHS = new Set([
  '/admin/partner',
  '/admin/document-fields',
  '/admin/system',
  '/admin/desktop-app',
  '/admin/health',
]);

/** Routes that only `SETTINGS_ROLES` may reach (gated by `RequireRole`) — every
 * Settings sub-screen requires a tenant_admin-only permission just to load. */
const SETTINGS_PATHS = new Set([
  '/settings/sources',
  '/settings/desktop',
  '/settings/mailboxes',
  '/settings/locations',
  '/settings/retention',
  '/settings/users',
  '/settings/branding',
  '/settings/webhooks',
]);

/** Screens with a real Phase-3 body, rendered instead of the placeholder. */
const BUILT_SCREENS: Record<string, React.JSX.Element> = {
  '/dashboard': <DashboardScreen />,
  '/documents': <DocumentsScreen />,
  '/documents/:documentId': <DocumentDetailScreen />,
  '/documents/exports/:exportId': <ExportDownloadScreen />,
  '/review': <ReviewQueueScreen />,
  '/review/:reviewItemId': <ReviewerScreen />,
  '/taxonomy': <TaxonomyScreen />,
  '/automation': <AutomationScreen />,
  '/settings/sources': <SourcesScreen />,
  '/settings/desktop': <DesktopUploaderScreen />,
  '/settings/mailboxes': <MailboxesScreen />,
  '/settings/locations': <LocationsScreen />,
  '/settings/retention': <RetentionScreen />,
  '/settings/users': <UsersScreen />,
  '/settings/branding': <BrandingScreen />,
  '/settings/webhooks': <WebhooksScreen />,
  '/admin/partner': <PartnerScreen />,
  '/admin/document-fields': <DocumentFieldsScreen />,
  '/admin/system': <SystemScreen />,
  '/admin/desktop-app': <DesktopAppScreen />,
  '/admin/health': <HealthScreen />,
  '/profile': <ProfileScreen />,
};

const adminScreens = SHELL_SCREENS.filter((screen) => ADMIN_PATHS.has(screen.path));
const settingsScreens = SHELL_SCREENS.filter((screen) => SETTINGS_PATHS.has(screen.path));
const openScreens = SHELL_SCREENS.filter(
  (screen) => !ADMIN_PATHS.has(screen.path) && !SETTINGS_PATHS.has(screen.path),
);

const placeholder = (screen: { path: string; title: string }): RouteObject => ({
  path: screen.path,
  element: BUILT_SCREENS[screen.path] ?? <ScreenPlaceholder title={screen.title} />,
});

/**
 * `/` serves two audiences: a signed-out visitor gets the public landing page,
 * a signed-in one is sent straight to their dashboard. A still-resolving session
 * (a stored token from a previous visit) waits inside `RequireAuth` rather than
 * flashing the marketing page at someone who is already a customer.
 */
function RootRoute(): React.JSX.Element {
  const { status, role } = useSession();

  if (status === 'unauthenticated') return <LandingScreen />;
  return (
    <RequireAuth>
      <Navigate to={landingPathForRole(role)} replace />
    </RequireAuth>
  );
}

/**
 * Client-side routes for the whole Docsight sitemap.
 *
 * `/` and `/login` render standalone. Everything else sits behind `RequireAuth` (an
 * unauthenticated user is bounced to /login) inside `AppShell`; the ADMIN and
 * Settings screens additionally sit behind `RequireRole`, mirroring the backend
 * RBAC so the UI never offers a route the API would refuse (they get /403 instead).
 */
const routeConfig: RouteObject[] = [
  { path: '/', element: <RootRoute /> },
  { path: LOGIN_PATH, element: <LoginScreen /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/settings', element: <Navigate to="/settings/sources" replace /> },
          { path: '/403', element: <ForbiddenScreen /> },
          ...openScreens.map(placeholder),
          {
            element: <RequireRole allow={ADMIN_ROLES} />,
            children: adminScreens.map(placeholder),
          },
          {
            element: <RequireRole allow={SETTINGS_ROLES} />,
            children: settingsScreens.map(placeholder),
          },
          { path: '*', element: <NotFoundScreen /> },
        ],
      },
    ],
  },
];

/** Renders the route tree for the current location. */
export function AppRoutes(): React.ReactElement | null {
  return useRoutes(routeConfig);
}
